import clsx from 'clsx'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

import { useNotchContext } from '../../contexts/use-notch-context'
import { useStep } from '../../hooks/use-step'
import { Step } from '../../utils/steps'
import { TitleAndText } from '../title-and-text/title-and-text'

import { FakeNotch } from './fake-notch/fake-notch'
import { TextField } from './text-field/text-field'

import style from './go-time.module.scss'

const getTitleAndTextHeight = () => {
  const h1Height = 48
  const textHeight = 25
  const gap = 24
  const padding = 32
  return h1Height + textHeight + gap + padding * 2
}

export const GoTime = () => {
  const { isSidePanelOpen, suppressRealNotch, setSuppressRealNotch } = useNotchContext()
  const { isLockedToGoTime, step } = useStep()
  const endRef = useRef<HTMLDivElement>(null)
  const [contentRef, setContentRef] = useState<HTMLDivElement | null>(null)
  const showTextField = () => endRef.current?.scrollIntoView()

  const titleAndTextHeight = getTitleAndTextHeight()

  useEffect(() => {
    if (isSidePanelOpen && contentRef && !contentRef.contains(document.activeElement)) {
      // if not already focused, focus the content
      contentRef.focus()
    }
    // contentRef.current can be set later than isSidePanelOpen
  }, [isSidePanelOpen, contentRef])

  return (
    <div
      data-step={Step.GoTime}
      className={clsx(style.goTimeContainer, step === Step.GoTime && style.maskFront)}
    >
      <AnimatePresence>
        {step === Step.GoTime && (
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{
              opacity: 1,
              y: isLockedToGoTime ? 0 : `calc(50% - ${titleAndTextHeight}px / 2)`,
            }}
            transition={{
              duration: 0.5,
              delay: isLockedToGoTime ? 0 : 0.5,
              y: { duration: isLockedToGoTime ? 0 : 0.5 },
            }}
            className={style.container}
            exit={{ opacity: 0, y: '100%', transition: { delay: 0 } }}
          >
            {!isLockedToGoTime && (
              <TitleAndText
                title="It's Go Time"
                text="Explore what you, your agents, and Go can do together."
                className={style.text}
                align="center"
              />
            )}
            <div className={style.textFieldContainer}>
              <TextField
                setContentRef={setContentRef}
                contentRef={contentRef}
                onFocus={showTextField}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {step === Step.GoTime && suppressRealNotch && (
        <FakeNotch
          onClick={() => {
            contentRef?.focus()
            setSuppressRealNotch(false)
          }}
        />
      )}
      <div ref={endRef} />
    </div>
  )
}
